import React, { useState } from "react";
import {
  ChefHat, Clock, Users, Leaf, Heart, Printer, Flame, Droplets,
  CheckCircle, XCircle, ChevronDown, ChevronUp, Star, AlertCircle, Share2
} from "lucide-react";

function PropertyBadge({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
        {label}
      </span>
      <span className="text-sm font-medium" style={{ color: "#2D3436", fontFamily: "Manrope, sans-serif" }}>
        {value}
      </span>
    </div>
  );
}

function StepItem({ step, index }) {
  return (
    <div className="recipe-step flex gap-4 py-3 group" style={{ borderBottom: "1px solid #F4F1EA" }}>
      <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
        style={{ backgroundColor: "#E07A5F1A", color: "#E07A5F", fontFamily: "Manrope, sans-serif" }}>
        {index + 1}
      </div>
      <p className="text-sm leading-relaxed pt-0.5" style={{ color: "#2D3436", fontFamily: "Manrope, sans-serif" }}>
        {step.replace(/^Step \d+:\s*/i, "")}
      </p>
    </div>
  );
}

function formatRecipeForWhatsApp(recipe) {
  const lines = [
    `*${recipe.recipe_name}* 🌿`,
    `_"${recipe.tagline}"_`,
    ``,
    `Serves ${recipe.servings} | Prep: ${recipe.prep_time} | Cook: ${recipe.cook_time}`,
    ``,
    `*Ingredients:*`,
    ...recipe.ingredients_used.map(i => `• ${i.name} — ${i.amount}`),
    ``,
    `*Method:*`,
    ...recipe.cooking_steps.map((s, idx) => `${idx + 1}. ${s.replace(/^Step \d+:\s*/i, "")}`),
    ``,
    `*Ayurvedic Profile:*`,
    `Rasa: ${recipe.ayurvedic_profile.dominant_rasa}`,
    `Virya: ${recipe.ayurvedic_profile.virya} | Vipaka: ${recipe.ayurvedic_profile.vipaka}`,
    ``,
    `*Why it works:* ${recipe.why_balanced}`,
    ``,
    `*Best enjoyed:* ${recipe.serving_suggestions}`,
    ``,
    `_Generated by RasaBox by ThatAyurvedaGuy_ 🌱`,
    `_Find your Prakriti: roadmap.thatayurvedaguy.com_`,
  ];
  return lines.join("\n");
}

function WhatsAppShareBar({ recipe }) {
  const shareOnWhatsApp = () => {
    const text = formatRecipeForWhatsApp(recipe);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3"
      style={{ backgroundColor: "#E7F5EE", border: "1px solid #C8E0D6" }}>
      <div className="flex items-center gap-3">
        {/* WhatsApp icon SVG */}
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "#25D366" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </div>
        <div>
          <p className="font-semibold text-sm" style={{ color: "#2D3436", fontFamily: "Manrope, sans-serif" }}>Share this recipe on WhatsApp</p>
          <p className="text-xs" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>Send to yourself or share with a friend</p>
        </div>
      </div>
      <button
        data-testid="whatsapp-share-btn"
        onClick={shareOnWhatsApp}
        className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all hover:scale-105 whitespace-nowrap"
        style={{ backgroundColor: "#25D366", color: "#FFFFFF", boxShadow: "0 4px 15px rgba(37,211,102,0.3)" }}
      >
        <Share2 size={14} />
        Share via WhatsApp
      </button>
    </div>
  );
}

export default function RecipeResult({ recipe, isSaved, onSaveToggle, user }) {
  const [showOmitted, setShowOmitted] = useState(false);

  const handlePrintDownload = () => {
    const printHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${recipe.recipe_name} — RasaBox by ThatAyurvedaGuy</title>
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Manrope:wght@400;500;600&display=swap" rel="stylesheet">
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Manrope',sans-serif; color:#2D3436; background:#fff; padding:48px; max-width:820px; margin:0 auto; }
    .brand { font-size:11px; color:#9AA5B1; text-transform:uppercase; letter-spacing:.1em; margin-bottom:8px; }
    h1 { font-family:'Playfair Display',serif; font-size:30px; color:#2D3436; margin-bottom:6px; }
    .tagline { color:#5D6D7E; font-style:italic; font-size:14px; margin-bottom:10px; }
    .meta { display:flex; gap:20px; font-size:12px; color:#9AA5B1; flex-wrap:wrap; margin-bottom:24px; padding-bottom:20px; border-bottom:2px solid #E07A5F; }
    h2 { font-family:'Playfair Display',serif; font-size:15px; color:#2D3436; margin-bottom:10px; padding-bottom:6px; border-bottom:1px solid #E5E0D8; margin-top:22px; }
    .grid { display:grid; grid-template-columns:2fr 3fr; gap:32px; }
    .ing-row { display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px solid #F4F1EA; }
    .ing-name { font-weight:600; font-size:13px; }
    .ing-amt { color:#E07A5F; font-weight:700; font-size:12px; }
    .ing-note { color:#9AA5B1; font-size:11px; margin-top:1px; }
    .step { display:flex; gap:10px; padding:7px 0; border-bottom:1px solid #F4F1EA; }
    .step-num { background:#E07A5F; color:#fff; min-width:20px; height:20px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; flex-shrink:0; margin-top:2px; }
    .step-txt { font-size:13px; line-height:1.6; }
    .profile { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-top:8px; }
    .p-lbl { font-size:10px; text-transform:uppercase; letter-spacing:.07em; color:#9AA5B1; }
    .p-val { font-size:13px; color:#2D3436; margin-top:2px; }
    .info-box { background:#F0F7F4; border-left:3px solid #81B29A; padding:12px 16px; font-size:13px; color:#5D6D7E; line-height:1.6; border-radius:4px; margin-top:8px; }
    .srv-box { background:#FFF9F0; border-left:3px solid #F2CC8F; padding:12px 16px; font-size:13px; color:#5D6D7E; line-height:1.6; border-radius:4px; margin-top:10px; }
    footer { margin-top:32px; padding-top:14px; border-top:1px solid #E5E0D8; color:#9AA5B1; font-size:11px; text-align:center; }
  </style>
</head>
<body>
  <p class="brand">RasaBox by ThatAyurvedaGuy</p>
  <h1>${recipe.recipe_name}</h1>
  <p class="tagline">"${recipe.tagline}"</p>
  <div class="meta">
    <span>Serves: <strong>${recipe.servings}</strong></span>
    <span>Prep: <strong>${recipe.prep_time}</strong></span>
    <span>Cook: <strong>${recipe.cook_time}</strong></span>
    <span>Style: <strong>${recipe.input_dish_type || "Chef's Choice"}</strong></span>
  </div>
  <div class="grid">
    <div>
      <h2>Ingredients</h2>
      ${recipe.ingredients_used.map(i => `
        <div class="ing-row">
          <div><div class="ing-name">${i.name}</div><div class="ing-note">${i.ayurvedic_note}</div></div>
          <span class="ing-amt">${i.amount}</span>
        </div>`).join("")}
    </div>
    <div>
      <h2>Method</h2>
      ${recipe.cooking_steps.map((s, i) => `
        <div class="step">
          <div class="step-num">${i + 1}</div>
          <p class="step-txt">${s.replace(/^Step \d+:\s*/i, "")}</p>
        </div>`).join("")}
    </div>
  </div>
  <h2>Ayurvedic Profile</h2>
  <div class="profile">
    <div><div class="p-lbl">Rasa (Taste)</div><div class="p-val">${recipe.ayurvedic_profile.dominant_rasa}</div></div>
    <div><div class="p-lbl">Virya (Potency)</div><div class="p-val">${recipe.ayurvedic_profile.virya}</div></div>
    <div><div class="p-lbl">Vipaka</div><div class="p-val">${recipe.ayurvedic_profile.vipaka}</div></div>
    <div><div class="p-lbl">Guna (Quality)</div><div class="p-val">${recipe.ayurvedic_profile.dominant_gunas}</div></div>
  </div>
  <h2>Why This Works</h2>
  <div class="info-box">${recipe.why_balanced}</div>
  <div class="srv-box"><strong>Serving Suggestion:</strong> ${recipe.serving_suggestions}</div>
  <footer>
    Generated by RasaBox by ThatAyurvedaGuy &nbsp;|&nbsp; Find your Prakriti: roadmap.thatayurvedaguy.com<br>
    Rooted in Charaka Samhita &amp; Ashtanga Hridayam
  </footer>
  <script>window.onload = function() { setTimeout(function(){ window.print(); }, 600); }</script>
</body>
</html>`;
    const pw = window.open("", "_blank", "width=900,height=700");
    if (pw) { pw.document.write(printHtml); pw.document.close(); }
  };

  return (
    <div className="space-y-4">
      {/* Recipe Header Card */}
      <div className="rounded-3xl p-8 relative overflow-hidden paper-texture"
        style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8", boxShadow: "0 4px 20px rgb(0,0,0,0.06)" }}>

        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl"
          style={{ background: `linear-gradient(90deg, #E07A5F, #F2CC8F, #81B29A)` }} />

        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide"
                style={{ backgroundColor: "#E07A5F20", color: "#E07A5F", border: "1px solid #E07A5F40" }}>
                <ChefHat size={10} />
                {recipe.input_dish_type || "Chef's Choice"}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full"
                style={{ backgroundColor: "#F4F1EA", color: "#5D6D7E" }}>
                <Users size={10} /> Serves {recipe.servings}
              </span>
            </div>
            <h2 className="font-serif font-bold mb-2" data-testid="recipe-name"
              style={{ fontSize: "clamp(1.5rem, 4vw, 2rem)", color: "#2D3436", lineHeight: 1.2 }}>
              {recipe.recipe_name}
            </h2>
            <p className="text-sm italic" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
              &ldquo;{recipe.tagline}&rdquo;
            </p>
          </div>

          {/* Action buttons — Save + Print */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="save-recipe-btn"
              onClick={() => onSaveToggle(recipe.id, recipe)}
              title={isSaved ? "Remove from RasaBox" : "Save to RasaBox"}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
              style={{
                backgroundColor: isSaved ? "#E07A5F" : "#FFF0EB",
                color: isSaved ? "#FFFFFF" : "#E07A5F",
                border: "1px solid #E07A5F40",
                fontFamily: "Manrope, sans-serif",
              }}
            >
              <Heart size={14} fill={isSaved ? "currentColor" : "none"} />
              <span className="hidden sm:inline">{isSaved ? "Saved" : "Save"}</span>
            </button>
            <button
              data-testid="print-recipe-btn"
              onClick={handlePrintDownload}
              title="Download / Print Recipe"
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
              style={{ backgroundColor: "#F4F1EA", color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}
            >
              <Printer size={14} />
              <span className="hidden sm:inline">Print</span>
            </button>
          </div>
        </div>

        {/* Times */}
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 text-sm" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
            <Clock size={14} style={{ color: "#E07A5F" }} />
            <span>Prep: <strong>{recipe.prep_time}</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
            <ChefHat size={14} style={{ color: "#E07A5F" }} />
            <span>Cook: <strong>{recipe.cook_time}</strong></span>
          </div>
        </div>
      </div>

      {/* Ayurvedic Fingerprint — prominent, above the bento grid */}
      <div className="rounded-2xl overflow-hidden"
        style={{ border: "1px solid #E5E0D8", backgroundColor: "#FFFFFF" }}>
        {/* Header bar */}
        <div className="px-5 py-3 flex items-center justify-between"
          style={{ backgroundColor: "#FFF9F0", borderBottom: "1px solid #F2CC8F50" }}>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "#F2CC8F60" }}>
              <Star size={11} style={{ color: "#B8860B" }} />
            </div>
            <h3 className="font-serif font-semibold text-sm" style={{ color: "#2D3436" }}>
              Ayurvedic Fingerprint
            </h3>
          </div>
          <span className="text-xs" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
            Rasa &middot; Virya &middot; Vipaka &middot; Guna
          </span>
        </div>

        {/* 4-column properties */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0"
          style={{ borderColor: "#F4F1EA" }}>
          {/* Rasa */}
          <div className="p-4">
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
              Rasa (Taste)
            </p>
            <p className="text-sm leading-relaxed font-medium" style={{ color: "#2D3436", fontFamily: "Manrope, sans-serif" }}>
              {recipe.ayurvedic_profile.dominant_rasa}
            </p>
          </div>

          {/* Virya */}
          <div className="p-4">
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
              Virya (Potency)
            </p>
            {(() => {
              const virya = recipe.ayurvedic_profile.virya || "";
              const isHeating = /ushna|heat|warm/i.test(virya);
              const isCooling = /sheeta|cool/i.test(virya);
              return (
                <div className="flex items-center gap-1.5">
                  {isHeating && <Flame size={14} style={{ color: "#E07A5F" }} strokeWidth={1.5} />}
                  {isCooling && <Droplets size={14} style={{ color: "#7EB3D4" }} strokeWidth={1.5} />}
                  {!isHeating && !isCooling && <Star size={14} style={{ color: "#F2CC8F" }} strokeWidth={1.5} />}
                  <span className="text-sm font-medium"
                    style={{ color: isHeating ? "#E07A5F" : isCooling ? "#7EB3D4" : "#2D3436", fontFamily: "Manrope, sans-serif" }}>
                    {virya}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Vipaka */}
          <div className="p-4">
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
              Vipaka (Post-Digestive)
            </p>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
              style={{ backgroundColor: "#81B29A20", color: "#5B8A6F", border: "1px solid #81B29A40", fontFamily: "Manrope, sans-serif" }}>
              {recipe.ayurvedic_profile.vipaka}
            </span>
          </div>

          {/* Guna */}
          <div className="p-4">
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
              Guna (Qualities)
            </p>
            <div className="flex flex-wrap gap-1">
              {recipe.ayurvedic_profile.dominant_gunas.split(/[,·]/).map((g, i) => (
                <span key={i} className="inline-block text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: "#F4F1EA", color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
                  {g.trim()}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bento Grid — Ingredients + Steps */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">

        {/* Ingredients — 2 cols */}
        <div className="md:col-span-2 rounded-2xl p-6"
          style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8", boxShadow: "0 4px 20px rgb(0,0,0,0.04)" }}>
          <div className="flex items-center gap-2 mb-4">
            <Leaf size={16} style={{ color: "#81B29A" }} strokeWidth={1.5} />
            <h3 className="font-serif font-semibold text-base" style={{ color: "#2D3436" }}>Ingredients</h3>
          </div>
          <div className="space-y-3">
            {recipe.ingredients_used.map((item, i) => (
              <div key={i} className="animate-fade-up opacity-0"
                style={{ animationDelay: `${i * 0.08}s`, animationFillMode: "forwards" }}>
                <div className="flex justify-between items-baseline mb-0.5">
                  <span className="font-medium text-sm capitalize" style={{ color: "#2D3436", fontFamily: "Manrope, sans-serif" }}>
                    {item.name}
                  </span>
                  <span className="text-xs font-semibold" style={{ color: "#E07A5F", fontFamily: "Manrope, sans-serif" }}>
                    {item.amount}
                  </span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
                  {item.ayurvedic_note}
                </p>
              </div>
            ))}
          </div>

          {/* Omitted ingredients toggle */}
          {recipe.ingredients_omitted && recipe.ingredients_omitted.length > 0 && (
            <div className="mt-4 pt-4" style={{ borderTop: "1px solid #F4F1EA" }}>
              <button
                data-testid="toggle-omitted-btn"
                onClick={() => setShowOmitted(!showOmitted)}
                className="flex items-center gap-2 text-xs font-medium transition-colors"
                style={{ color: "#E07A5F", fontFamily: "Manrope, sans-serif" }}
              >
                <XCircle size={14} />
                {recipe.ingredients_omitted.length} ingredient{recipe.ingredients_omitted.length > 1 ? "s" : ""} omitted
                {showOmitted ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
              {showOmitted && (
                <div className="mt-3 space-y-2 animate-fade-in">
                  {recipe.ingredients_omitted.map((item, i) => (
                    <div key={i} className="p-3 rounded-xl"
                      style={{ backgroundColor: "#FFF8F6", border: "1px solid #F2C4BA" }}>
                      <p className="font-semibold text-xs capitalize mb-0.5" style={{ color: "#C0392B" }}>
                        {item.name}
                      </p>
                      <p className="text-xs" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
                        {item.reason}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cooking Steps — 3 cols */}
        <div className="md:col-span-3 rounded-2xl p-6"
          style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8", boxShadow: "0 4px 20px rgb(0,0,0,0.04)" }}>
          <div className="flex items-center gap-2 mb-4">
            <ChefHat size={16} style={{ color: "#E07A5F" }} strokeWidth={1.5} />
            <h3 className="font-serif font-semibold text-base" style={{ color: "#2D3436" }}>Method</h3>
          </div>
          <div>
            {recipe.cooking_steps.map((step, i) => (
              <StepItem key={i} step={step} index={i} />
            ))}
          </div>
        </div>
      </div>

      {/* Why Balanced + Serving + Caution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl p-6 space-y-3"
          style={{ backgroundColor: "#F0F7F4", border: "1px solid #C8E0D6" }}>
          <div className="flex items-center gap-2">
            <CheckCircle size={16} style={{ color: "#81B29A" }} strokeWidth={1.5} />
            <h4 className="font-serif font-semibold text-sm" style={{ color: "#2D3436" }}>Why This Works</h4>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
            {recipe.why_balanced}
          </p>
          <div className="pt-2" style={{ borderTop: "1px solid #C8E0D6" }}>
            <p className="text-xs font-semibold mb-1 uppercase tracking-widest" style={{ color: "#9AA5B1" }}>Dosha Balance</p>
            <p className="text-sm" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>{recipe.dosha_balance}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl p-5"
            style={{ backgroundColor: "#FFF9F0", border: "1px solid #F2CC8F80" }}>
            <h4 className="font-serif font-semibold text-sm mb-2" style={{ color: "#2D3436" }}>Serving Suggestions</h4>
            <p className="text-sm leading-relaxed" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
              {recipe.serving_suggestions}
            </p>
          </div>

          {recipe.who_should_avoid && recipe.who_should_avoid !== "None noted" && (
            <div className="rounded-2xl p-5 flex gap-3"
              style={{ backgroundColor: "#FFF8F6", border: "1px solid #F2C4BA" }}>
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" style={{ color: "#C0392B" }} strokeWidth={1.5} />
              <div>
                <h4 className="font-serif font-semibold text-sm mb-1" style={{ color: "#C0392B" }}>Mindful Of</h4>
                <p className="text-xs leading-relaxed" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
                  {recipe.who_should_avoid}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Share */}
      <WhatsAppShareBar recipe={recipe} />
    </div>
  );
}
